// ===================================================================
// HMGP — Reimbursement Requests (RFR) Management
// Full-featured: create, edit, review (approve/deny/revision),
// cost breakdown line items, mark-as-paid, CSV export
// ===================================================================

import { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Title, Text, Paper, Group, Stack, Badge, Button, TextInput, Select, Textarea,
  NumberInput, Modal, Table, SimpleGrid, ActionIcon, Center, Loader, Alert, Divider,
  Checkbox,
} from '../../../components/gab/GabComponents';
import { notifications } from '../../../lib/notifications';
import {
  IconPlus, IconSearch, IconDownload, IconReceipt, IconTrash, IconSend, IconEdit,
  IconAlertCircle, IconArrowRight, IconCheck, IconX, IconArrowBack, IconCash,
  IconRowInsertBottom,
} from '@tabler/icons-react';
import { useNavigate } from 'react-router-dom';
import { useHMGPStore } from '../../../store/hmgpStore';
import { usePAGrantsStore } from '../../../store/paGrantsStore';
import { useAuthStore } from '../../../store/authStore';
import { useRoleStore } from '../../../store/roleStore';
import { useMyPAOrg } from '../../../hooks/useMyPAOrg';
import { useActivePADisaster as useActiveDisaster } from '../../../hooks/useActiveDisaster';
import { exportToCSV } from '../../../lib/exportUtils';
import type { PAReimbursementRequest } from '../../../types/pagrants';

// ── Constants ───────────────────────────────────────────────────

const GREEN = '#059669';

const STATUS_COLORS: Record<string, string> = {
  draft: '#939598', submitted: 'var(--color-primary-700)', under_review: '#C68700',
  approved: 'var(--color-success)', denied: 'var(--color-error)', revision_needed: '#F86A0B', paid: '#03AC9E',
};

const STATUS_LABELS: Record<string, string> = {
  draft: 'Draft', submitted: 'Submitted', under_review: 'Under Review',
  approved: 'Approved', denied: 'Denied', revision_needed: 'Revision Needed', paid: 'Paid',
};

function fmtCurrency(v: string | number): string {
  const n = typeof v === 'string' ? parseFloat(v) : v;
  if (isNaN(n) || n === 0) return '$0';
  return n.toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });
}

function num(v: string | number | undefined): number {
  if (v === undefined || v === null || v === '') return 0;
  const n = typeof v === 'string' ? parseFloat(v) : v;
  return isNaN(n) ? 0 : n;
}

// ── Cost Breakdown ──────────────────────────────────────────────

interface CostLineItem {
  description: string;
  amount: number;
}

function parseCostBreakdown(raw: string): CostLineItem[] {
  if (!raw) return [];
  try {
    const parsed = JSON.parse(raw);
    if (Array.isArray(parsed)) return parsed;
  } catch { /* ignore */ }
  return [];
}

function serializeCostBreakdown(items: CostLineItem[]): string {
  const filtered = items.filter(i => i.description || i.amount);
  return filtered.length > 0 ? JSON.stringify(filtered) : '';
}

// ── Form State ──────────────────────────────────────────────────

interface RFRForm {
  projectId: string;
  periodStart: string;
  periodEnd: string;
  notes: string;
  costItems: CostLineItem[];
  manualTotal: number | '';
  useManualTotal: boolean;
}

const emptyForm = (): RFRForm => ({
  projectId: '', periodStart: '', periodEnd: '', notes: '',
  costItems: [{ description: '', amount: 0 }],
  manualTotal: '', useManualTotal: false,
});

// ═════════════════════════════════════════════════════════════════
//  COMPONENT
// ═════════════════════════════════════════════════════════════════

export default function HMGPReimbursements() {
  const navigate = useNavigate();
  const { projects, fetchProjects } = useHMGPStore();
  const {
    reimbursementRequests, projectCosts, organizations,
    fetchReimbursements, fetchOrganizations, fetchProjectCosts,
    createReimbursement, deleteReimbursement, updateReimbursement,
  } = usePAGrantsStore();
  const user = useAuthStore(s => s.user);
  const effectiveRole = useRoleStore(s => s.demoRoleOverride ?? s.myRole);
  const isApplicant = ['pa_applicant_mgr', 'pa_applicant_staff', 'pa_applicant_viewer'].includes(effectiveRole?.roleSlug ?? '');
  const isViewer = effectiveRole?.roleSlug === 'pa_applicant_viewer';
  const myOrg = useMyPAOrg();
  const activeDisaster = useActiveDisaster();

  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<string | null>(null);

  // Create modal
  const [createOpen, setCreateOpen] = useState(false);
  const [form, setForm] = useState<RFRForm>(emptyForm());
  const [saving, setSaving] = useState(false);

  // Detail/Edit modal
  const [detailRfr, setDetailRfr] = useState<PAReimbursementRequest | null>(null);
  const [editMode, setEditMode] = useState(false);
  const [editForm, setEditForm] = useState<RFRForm>(emptyForm());

  // Review state (inside detail modal)
  const [reviewNotes, setReviewNotes] = useState('');
  const [adjustedAmount, setAdjustedAmount] = useState<number | ''>('');
  // Per-line-item review: 'approved' | 'rejected' | undefined (pending)
  const [lineItemStatuses, setLineItemStatuses] = useState<Record<number, 'approved' | 'rejected'>>({});

  // Payment state
  const [paymentRef, setPaymentRef] = useState('');
  const [paymentDate, setPaymentDate] = useState('');

  useEffect(() => {
    Promise.all([
      fetchProjects(), fetchReimbursements(), fetchOrganizations(), fetchProjectCosts(),
    ]).finally(() => setLoading(false));
  }, []);

  // Filter projects by active disaster
  const filteredProjects = useMemo(() => {
    if (!activeDisaster) return [];
    return projects.filter(p => String(p.disasterId) === String(activeDisaster.Rid));
  }, [projects, activeDisaster]);

  // Set of HMGP project Rids for filtering reimbursements
  const hmgpProjectRids = useMemo(() => {
    const set = new Set<string>();
    filteredProjects.forEach(p => set.add(String(p.Rid)));
    return set;
  }, [filteredProjects]);

  // Filter reimbursements to those linked to HMGP projects
  const hmgpReimbursements = useMemo(() => {
    let list = reimbursementRequests.filter(r => hmgpProjectRids.has(String(r.projectId)));
    // Applicants see only their org's RFRs
    if (isApplicant && myOrg) {
      const myOrgId = String(myOrg.Rid);
      list = list.filter(r => {
        const proj = filteredProjects.find(p => String(p.Rid) === String(r.projectId));
        return proj && String(proj.organizationId) === myOrgId;
      });
    }
    return list;
  }, [reimbursementRequests, hmgpProjectRids, isApplicant, myOrg, filteredProjects]);

  // Projects available for creating RFRs (applicants see only their org's)
  const availableProjects = useMemo(() => {
    if (isApplicant && myOrg) {
      return filteredProjects.filter(p => String(p.organizationId) === String(myOrg.Rid));
    }
    return filteredProjects;
  }, [filteredProjects, isApplicant, myOrg]);

  // Project lookup map
  const projectMap = useMemo(() => {
    const m: Record<string, (typeof filteredProjects)[0]> = {};
    filteredProjects.forEach(p => { m[String(p.Rid)] = p; });
    return m;
  }, [filteredProjects]);

  // Apply search and status filter
  const displayList = useMemo(() => {
    let list = hmgpReimbursements;
    if (statusFilter) list = list.filter(r => r.status === statusFilter);
    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter(r => {
        const proj = projectMap[r.projectId];
        return (
          (r.requestNumber || '').toLowerCase().includes(q) ||
          (proj?.projectTitle || '').toLowerCase().includes(q) ||
          (r.status || '').toLowerCase().includes(q)
        );
      });
    }
    return list;
  }, [hmgpReimbursements, statusFilter, search, projectMap]);

  // ── Stats ─────────────────────────────────────────────────────

  const stats = useMemo(() => {
    const totalRequested = hmgpReimbursements.reduce((s, r) => s + num(r.requestedAmount), 0);
    const approved = hmgpReimbursements.filter(r => r.status === 'approved' || r.status === 'paid');
    const totalApproved = approved.reduce((s, r) => s + num(r.approvedAmount || r.requestedAmount), 0);
    const paid = hmgpReimbursements.filter(r => r.status === 'paid');
    const totalPaid = paid.reduce((s, r) => s + num(r.approvedAmount || r.requestedAmount), 0);
    const pendingReview = hmgpReimbursements.filter(r =>
      r.status === 'submitted' || r.status === 'under_review'
    ).length;
    return { totalRequested, totalApproved, totalPaid, pendingReview };
  }, [hmgpReimbursements]);

  // ── Helpers ───────────────────────────────────────────────────

  function nextRFRNumber(projId: string): string {
    const proj = projectMap[projId];
    const prefix = `RFR-HMGP-${proj?.projectNumber || '000'}`;
    const nums = hmgpReimbursements
      .filter(r => r.requestNumber.startsWith(prefix))
      .map(r => parseInt(r.requestNumber.split('-').pop() || '0'))
      .filter(n => !isNaN(n));
    const next = nums.length > 0 ? Math.max(...nums) + 1 : 1;
    return `${prefix}-${String(next).padStart(3, '0')}`;
  }

  function computeTotals(f: RFRForm) {
    if (f.useManualTotal && typeof f.manualTotal === 'number' && f.manualTotal > 0) {
      return f.manualTotal;
    }
    return f.costItems.reduce((s, i) => s + (i.amount || 0), 0);
  }

  function computeShares(total: number, projId: string) {
    const proj = projectMap[projId];
    const fedPct = num(proj?.federalSharePct || (activeDisaster as unknown as Record<string, string>)?.federalSharePct || '75');
    const fedShare = total * (fedPct / 100);
    return { fedShare, applicantMatch: total - fedShare, fedPct };
  }

  const refreshAndReselect = useCallback(async (rid: number) => {
    await fetchReimbursements();
    const fresh = usePAGrantsStore.getState().reimbursementRequests.find(r => r.Rid === rid);
    setDetailRfr(fresh || null);
  }, [fetchReimbursements]);

  // ── Cost picker state ────────────────────────────────────────
  const [costPickerOpen, setCostPickerOpen] = useState(false);
  const [costPickerProjId, setCostPickerProjId] = useState('');
  const [costPickerSetItems, setCostPickerSetItems] = useState<((items: CostLineItem[]) => void) | null>(null);
  const [selectedCostRids, setSelectedCostRids] = useState<Set<number>>(new Set());
  const [costPickerCurrentItems, setCostPickerCurrentItems] = useState<CostLineItem[]>([]);

  // Build previously-reimbursed cost descriptions per project
  // Parses costBreakdown from all non-draft RFRs for the same project
  const previouslyReimbursedMap = useMemo(() => {
    const map = new Map<string, { projId: string; descriptions: Set<string>; totalByDesc: Map<string, number> }>();
    for (const rfr of hmgpReimbursements) {
      if (rfr.status === 'draft') continue; // drafts don't count
      const key = rfr.projectId;
      if (!map.has(key)) map.set(key, { projId: key, descriptions: new Set(), totalByDesc: new Map() });
      const entry = map.get(key)!;
      const items = parseCostBreakdown(rfr.costBreakdown);
      for (const item of items) {
        entry.descriptions.add(item.description);
        entry.totalByDesc.set(item.description, (entry.totalByDesc.get(item.description) || 0) + item.amount);
      }
    }
    return map;
  }, [hmgpReimbursements]);

  function openCostPicker(projId: string, currentItems: CostLineItem[], setItems: (items: CostLineItem[]) => void) {
    setCostPickerProjId(projId);
    setCostPickerSetItems(() => setItems);
    setCostPickerCurrentItems(currentItems);
    setSelectedCostRids(new Set());
    setCostPickerOpen(true);
  }

  function handleCostPickerImport() {
    if (!costPickerSetItems || selectedCostRids.size === 0) return;
    const selected = projectCosts
      .filter(c => selectedCostRids.has(c.Rid))
      .map(c => ({
        description: `${c.costType || 'Cost'}: ${c.description || 'Untitled'}`,
        amount: num(c.totalCost),
      }));
    const existing = new Set(costPickerCurrentItems.map(i => i.description));
    const newItems = selected.filter(s => !existing.has(s.description));
    const cleaned = costPickerCurrentItems.filter(i => i.description || i.amount);
    costPickerSetItems([...cleaned, ...newItems]);
    notifications.show({ title: 'Imported', message: `Added ${selected.length} cost line(s)`, color: 'var(--color-success)' });
    setCostPickerOpen(false);
  }

  // ── CREATE ────────────────────────────────────────────────────

  async function handleCreate() {
    if (!form.projectId) {
      notifications.show({ title: 'Error', message: 'Select a project', color: 'var(--color-error)' });
      return;
    }
    const total = computeTotals(form);
    if (total <= 0) {
      notifications.show({ title: 'Error', message: 'Total requested amount must be greater than zero', color: 'var(--color-error)' });
      return;
    }
    if (!activeDisaster) return;

    setSaving(true);
    try {
      const proj = projectMap[form.projectId];
      const { fedShare, applicantMatch } = computeShares(total, form.projectId);

      await createReimbursement({
        projectId: form.projectId,
        organizationId: proj?.organizationId || '',
        disasterId: String(activeDisaster.Rid),
        requestNumber: nextRFRNumber(form.projectId),
        requestDate: new Date().toISOString().split('T')[0],
        periodStart: form.periodStart,
        periodEnd: form.periodEnd,
        requestedAmount: String(total),
        federalShare: String(fedShare.toFixed(2)),
        applicantMatch: String(applicantMatch.toFixed(2)),
        costBreakdown: serializeCostBreakdown(form.costItems),
        supportingDocIds: '',
        submittedBy: '',
        submittedDate: '',
        status: 'draft',
        reviewedBy: '', reviewDate: '', reviewNotes: '',
        approvedAmount: '',
        paymentDate: '', paymentReference: '',
        notes: form.notes,
      });

      notifications.show({ title: 'Created', message: 'Reimbursement request created', color: 'var(--color-success)' });
      setCreateOpen(false);
      setForm(emptyForm());
      fetchReimbursements();
    } catch {
      notifications.show({ title: 'Error', message: 'Failed to create reimbursement request', color: 'var(--color-error)' });
    } finally {
      setSaving(false);
    }
  }

  // ── SAVE EDIT ─────────────────────────────────────────────────

  async function handleSaveEdit(submitAfter = false) {
    if (!detailRfr) return;
    const total = computeTotals(editForm);
    if (total <= 0) {
      notifications.show({ title: 'Error', message: 'Total must be greater than zero', color: 'var(--color-error)' });
      return;
    }

    setSaving(true);
    try {
      const { fedShare, applicantMatch } = computeShares(total, editForm.projectId || detailRfr.projectId);
      const updates: Partial<PAReimbursementRequest> = {
        periodStart: editForm.periodStart,
        periodEnd: editForm.periodEnd,
        requestedAmount: String(total),
        federalShare: String(fedShare.toFixed(2)),
        applicantMatch: String(applicantMatch.toFixed(2)),
        costBreakdown: serializeCostBreakdown(editForm.costItems),
        notes: editForm.notes,
      };
      if (submitAfter) {
        updates.status = 'submitted';
        updates.submittedBy = user?.fullName || user?.email || '';
        updates.submittedDate = new Date().toISOString().split('T')[0];
      }

      await updateReimbursement(detailRfr.Rid, updates);
      notifications.show({ title: 'Saved', message: submitAfter ? 'RFR submitted for review' : 'Changes saved', color: 'var(--color-success)' });
      setEditMode(false);
      await refreshAndReselect(detailRfr.Rid);
    } catch {
      notifications.show({ title: 'Error', message: 'Failed to save changes', color: 'var(--color-error)' });
    } finally {
      setSaving(false);
    }
  }

  // ── SUBMIT ────────────────────────────────────────────────────

  async function handleSubmit() {
    if (!detailRfr) return;
    setSaving(true);
    try {
      await updateReimbursement(detailRfr.Rid, {
        status: 'submitted',
        submittedBy: user?.fullName || user?.email || '',
        submittedDate: new Date().toISOString().split('T')[0],
      });
      notifications.show({ title: 'Submitted', message: 'RFR submitted for review', color: 'var(--color-success)' });
      await refreshAndReselect(detailRfr.Rid);
    } catch {
      notifications.show({ title: 'Error', message: 'Failed to submit RFR', color: 'var(--color-error)' });
    } finally {
      setSaving(false);
    }
  }

  // ── REVIEW ACTIONS (grantee/admin) ────────────────────────────

  async function handleBeginReview() {
    if (!detailRfr) return;
    setSaving(true);
    try {
      await updateReimbursement(detailRfr.Rid, { status: 'under_review' });
      await refreshAndReselect(detailRfr.Rid);
    } catch {
      notifications.show({ title: 'Error', message: 'Failed to begin review', color: 'var(--color-error)' });
    } finally {
      setSaving(false);
    }
  }



  async function handleDeny() {
    if (!detailRfr) return;
    if (!reviewNotes.trim()) {
      notifications.show({ title: 'Notes required', message: 'Please provide a reason for denial.', color: '#F86A0B' });
      return;
    }
    setSaving(true);
    try {
      await updateReimbursement(detailRfr.Rid, {
        status: 'denied',
        reviewedBy: user?.fullName || user?.email || '',
        reviewDate: new Date().toISOString().split('T')[0],
        reviewNotes,
      });
      notifications.show({ title: 'Denied', message: 'RFR has been denied', color: 'var(--color-error)' });
      setReviewNotes('');
      await refreshAndReselect(detailRfr.Rid);
    } catch {
      notifications.show({ title: 'Error', message: 'Failed to deny RFR', color: 'var(--color-error)' });
    } finally {
      setSaving(false);
    }
  }

  async function handleRequestRevision() {
    if (!detailRfr) return;
    if (!reviewNotes.trim()) {
      notifications.show({ title: 'Notes required', message: 'Please explain what revision is needed.', color: '#F86A0B' });
      return;
    }
    setSaving(true);
    try {
      await updateReimbursement(detailRfr.Rid, {
        status: 'revision_needed',
        reviewedBy: user?.fullName || user?.email || '',
        reviewDate: new Date().toISOString().split('T')[0],
        reviewNotes,
      });
      notifications.show({ title: 'Revision Requested', message: 'RFR sent back for revision', color: '#F86A0B' });
      setReviewNotes('');
      await refreshAndReselect(detailRfr.Rid);
    } catch {
      notifications.show({ title: 'Error', message: 'Failed to request revision', color: 'var(--color-error)' });
    } finally {
      setSaving(false);
    }
  }

  // ── MARK AS PAID ──────────────────────────────────────────────

  async function handleMarkPaid() {
    if (!detailRfr) return;
    setSaving(true);
    try {
      await updateReimbursement(detailRfr.Rid, {
        status: 'paid',
        paymentDate: paymentDate || new Date().toISOString().split('T')[0],
        paymentReference: paymentRef,
      });
      notifications.show({ title: 'Paid', message: 'RFR marked as paid', color: '#03AC9E' });
      setPaymentRef('');
      setPaymentDate('');
      await refreshAndReselect(detailRfr.Rid);
    } catch {
      notifications.show({ title: 'Error', message: 'Failed to mark as paid', color: 'var(--color-error)' });
    } finally {
      setSaving(false);
    }
  }

  // ── DELETE ────────────────────────────────────────────────────

  async function handleDelete(rfr: PAReimbursementRequest) {
    if (rfr.status !== 'draft') {
      notifications.show({ title: 'Error', message: 'Only draft RFRs can be deleted', color: 'var(--color-error)' });
      return;
    }
    if (!confirm('Delete this reimbursement request?')) return;
    try {
      await deleteReimbursement(rfr.Rid);
      notifications.show({ title: 'Deleted', message: 'Reimbursement request deleted', color: 'var(--color-success)' });
      setDetailRfr(null);
      fetchReimbursements();
    } catch {
      notifications.show({ title: 'Error', message: 'Failed to delete', color: 'var(--color-error)' });
    }
  }

  // ── EXPORT ────────────────────────────────────────────────────

  function handleExport() {
    const headers = ['Request #', 'Project', 'Requested', 'Approved', 'Federal Share', 'Status', 'Submitted', 'Payment Date'];
    const rows = displayList.map(r => {
      const proj = projectMap[r.projectId];
      return [
        r.requestNumber, proj?.projectTitle || r.projectId,
        fmtCurrency(r.requestedAmount), fmtCurrency(r.approvedAmount),
        fmtCurrency(r.federalShare),
        STATUS_LABELS[r.status] || r.status, r.submittedDate || '', r.paymentDate || '',
      ];
    });
    exportToCSV('hmgp-reimbursements', headers, rows);
  }

  // ── OPEN DETAIL ───────────────────────────────────────────────

  function openDetail(rfr: PAReimbursementRequest) {
    setDetailRfr(rfr);
    setEditMode(false);
    setReviewNotes('');
    setAdjustedAmount('');
    setPaymentRef('');
    setPaymentDate('');
    // Pre-populate line item statuses: all approved by default for under_review
    if (rfr.status === 'under_review') {
      const items = parseCostBreakdown(rfr.costBreakdown);
      const statuses: Record<number, 'approved'> = {};
      items.forEach((_, i) => { statuses[i] = 'approved'; });
      setLineItemStatuses(statuses);
    } else {
      setLineItemStatuses({});
    }
  }

  function enterEditMode(rfr: PAReimbursementRequest) {
    const costItems = parseCostBreakdown(rfr.costBreakdown);
    setEditForm({
      projectId: rfr.projectId,
      periodStart: rfr.periodStart || '',
      periodEnd: rfr.periodEnd || '',
      notes: rfr.notes || '',
      costItems: costItems.length > 0 ? costItems : [{ description: '', amount: 0 }],
      manualTotal: '',
      useManualTotal: false,
    });
    setEditMode(true);
  }

  // ── COST LINE ITEM EDITOR (shared sub-component) ─────────────

  function renderCostEditor(
    items: CostLineItem[],
    setItems: (items: CostLineItem[]) => void,
    projId: string,
  ) {
    const lineTotal = items.reduce((s, i) => s + (i.amount || 0), 0);

    return (
      <Stack gap="xs">
        <Group justify="space-between" mb={4}>
          <Text size="sm" fw={600}>Cost Breakdown</Text>
          <Badge variant="light" color="var(--color-success)" size="lg">Total: {fmtCurrency(lineTotal)}</Badge>
        </Group>

        {items.map((item, idx) => (
          <Group key={idx} gap="xs" align="flex-end">
            <TextInput
              label={idx === 0 ? 'Description' : undefined}
              placeholder="Line item description"
              value={item.description}
              onChange={e => {
                const next = [...items];
                next[idx] = { ...next[idx], description: e.target.value };
                setItems(next);
              }}
              style={{ flex: 3 }}
              size="sm"
            />
            <NumberInput
              label={idx === 0 ? 'Amount' : undefined}
              placeholder="0.00"
              prefix="$"
              thousandSeparator=","
              min={0}
              value={item.amount || ''}
              onChange={v => {
                const next = [...items];
                next[idx] = { ...next[idx], amount: typeof v === 'number' ? v : 0 };
                setItems(next);
              }}
              style={{ flex: 1, minWidth: 130 }}
              size="sm"
            />
            <ActionIcon
              size="sm" variant="subtle" color="var(--color-error)"
              onClick={() => {
                const next = items.filter((_, i) => i !== idx);
                setItems(next.length > 0 ? next : [{ description: '', amount: 0 }]);
              }}
            >
              <IconTrash size={14} />
            </ActionIcon>
          </Group>
        ))}

        <Group gap="sm">
          <Button
            variant="light" size="xs" color="var(--color-success)"
            leftSection={<IconPlus size={14} />}
            onClick={() => setItems([...items, { description: '', amount: 0 }])}
          >
            Add Line Item
          </Button>
          {projId && (
            <Button
              variant="subtle" size="xs" color="var(--color-primary-700)"
              leftSection={<IconRowInsertBottom size={14} />}
              onClick={() => openCostPicker(projId, items, setItems)}
            >
              Select from Project Costs
            </Button>
          )}
        </Group>
      </Stack>
    );
  }

  // ═════════════════════════════════════════════════════════════
  //  RENDER
  // ═════════════════════════════════════════════════════════════

  if (loading) return <Center h={200}><Loader color="var(--color-success)" /></Center>;

  if (!activeDisaster) {
    return (
      <Stack p="md">
        <Title order={3}>HMGP Reimbursements</Title>
        <Alert icon={<IconAlertCircle size={16} />} color="#C68700" title="No Active Disaster">
          Select an active incident in the top bar to manage HMGP reimbursements.
        </Alert>
      </Stack>
    );
  }

  if (filteredProjects.length === 0 && !loading) {
    return (
      <Stack p="md">
        <Title order={3}>HMGP Reimbursements</Title>
        <Alert icon={<IconAlertCircle size={16} />} color="var(--color-primary-700)" title="No HMGP Projects">
          There are no HMGP projects for the active disaster. Create projects first before submitting reimbursement requests.
        </Alert>
      </Stack>
    );
  }

  return (
    <Stack p="md" gap="sm">
      <Group justify="space-between">
        <div>
          <Title order={3}>HMGP Reimbursements</Title>
          <Text size="sm" c="dimmed">Request for Reimbursement (RFR) management</Text>
        </div>
        <Group gap="sm">
          {!isViewer && (
            <Button leftSection={<IconPlus size={16} />} color="var(--color-success)" onClick={() => { setForm(emptyForm()); setCreateOpen(true); }}>
              New RFR
            </Button>
          )}
          <Button leftSection={<IconDownload size={16} />} variant="light" color="var(--color-success)" onClick={handleExport}>
            Export
          </Button>
        </Group>
      </Group>

      {/* Stat Cards */}
      <SimpleGrid cols={{ base: 2, sm: 4 }}>
        <Paper p="sm" withBorder ta="center" style={{ borderTop: `3px solid ${GREEN}` }}>
          <Text size="xs" c="dimmed">Total Requested</Text>
          <Text size="lg" fw={700}>{fmtCurrency(stats.totalRequested)}</Text>
        </Paper>
        <Paper p="sm" withBorder ta="center" style={{ borderTop: '3px solid #0284C7' }}>
          <Text size="xs" c="dimmed">Total Approved</Text>
          <Text size="lg" fw={700}>{fmtCurrency(stats.totalApproved)}</Text>
        </Paper>
        <Paper p="sm" withBorder ta="center" style={{ borderTop: '3px solid #10B981' }}>
          <Text size="xs" c="dimmed">Total Paid</Text>
          <Text size="lg" fw={700}>{fmtCurrency(stats.totalPaid)}</Text>
        </Paper>
        <Paper p="sm" withBorder ta="center" style={{ borderTop: '3px solid #D97706' }}>
          <Text size="xs" c="dimmed">Pending Review</Text>
          <Text size="lg" fw={700}>{stats.pendingReview}</Text>
        </Paper>
      </SimpleGrid>

      {/* Filters */}
      <Group gap="sm">
        <TextInput
          placeholder="Search RFRs..."
          leftSection={<IconSearch size={16} />}
          value={search}
          onChange={e => setSearch(e.target.value)}
          w={260}
        />
        <Select
          placeholder="All statuses"
          clearable
          value={statusFilter}
          onChange={setStatusFilter}
          data={Object.entries(STATUS_LABELS).map(([v, l]) => ({ value: v, label: l }))}
          w={180}
        />
        <Badge variant="light" color="#939598">{displayList.length} results</Badge>
      </Group>

      {/* Table */}
      {displayList.length === 0 ? (
        <Paper p="xl" withBorder>
          <Stack align="center" gap="sm">
            <IconReceipt size={48} color="#9CA3AF" />
            <Text c="dimmed">No reimbursement requests found.</Text>
            <Button leftSection={<IconPlus size={16} />} variant="light" color="var(--color-success)" onClick={() => { setForm(emptyForm()); setCreateOpen(true); }}>
              Start a New RFR
            </Button>
          </Stack>
        </Paper>
      ) : (
        <Paper withBorder style={{ overflow: 'hidden' }}>
          <div style={{ overflowX: 'auto', overflowY: 'auto', maxHeight: 'calc(100vh - 380px)' }}>
          <Table striped highlightOnHover withTableBorder withColumnBorders>
            <Table.Thead style={{ position: 'sticky', top: 0, zIndex: 1, background: '#F8F8F8' }}>
              <Table.Tr>
                <Table.Th>RFR #</Table.Th>
                <Table.Th>Project</Table.Th>
                {!isApplicant && <Table.Th>Applicant</Table.Th>}
                <Table.Th ta="right">Requested</Table.Th>
                <Table.Th ta="right">Federal Share</Table.Th>
                <Table.Th>Period</Table.Th>
                <Table.Th>Status</Table.Th>
                <Table.Th>Date</Table.Th>
                <Table.Th ta="center">Actions</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {displayList.map(r => {
                const proj = projectMap[r.projectId];
                return (
                  <Table.Tr
                    key={r.Rid}
                    style={{ cursor: 'pointer' }}
                    onClick={() => openDetail(r)}
                  >
                    <Table.Td fw={500}>{r.requestNumber}</Table.Td>
                    <Table.Td>{proj?.projectTitle || r.projectId}</Table.Td>
                    {!isApplicant && <Table.Td><Text size="sm" c="dimmed">{organizations.find(o => String(o.Rid) === proj?.organizationId)?.orgName || '—'}</Text></Table.Td>}
                    <Table.Td ta="right">{fmtCurrency(r.requestedAmount)}</Table.Td>
                    <Table.Td ta="right">{fmtCurrency(r.federalShare)}</Table.Td>
                    <Table.Td>
                      {r.periodStart && r.periodEnd
                        ? `${new Date(r.periodStart).toLocaleDateString()} - ${new Date(r.periodEnd).toLocaleDateString()}`
                        : r.periodStart ? new Date(r.periodStart).toLocaleDateString() : r.periodEnd ? new Date(r.periodEnd).toLocaleDateString() : '-'}
                    </Table.Td>
                    <Table.Td>
                      <Badge size="sm" color={STATUS_COLORS[r.status] || '#939598'} variant="light">
                        {STATUS_LABELS[r.status] || r.status}
                      </Badge>
                    </Table.Td>
                    <Table.Td>{r.submittedDate ? new Date(r.submittedDate).toLocaleDateString() : r.requestDate ? new Date(r.requestDate).toLocaleDateString() : '-'}</Table.Td>
                    <Table.Td ta="center">
                      <Group gap={4} justify="center" onClick={e => e.stopPropagation()}>
                        {proj && (
                          <ActionIcon
                            size="sm" variant="subtle" color="var(--color-success)"
                            title="View Project"
                            onClick={() => navigate(`/pagrants/hmgp/projects/${proj.Rid}`)}
                          >
                            <IconArrowRight size={14} />
                          </ActionIcon>
                        )}
                        {r.status === 'draft' && (
                          <ActionIcon size="sm" variant="subtle" color="var(--color-error)" title="Delete" onClick={() => handleDelete(r)}>
                            <IconTrash size={14} />
                          </ActionIcon>
                        )}
                      </Group>
                    </Table.Td>
                  </Table.Tr>
                );
              })}
            </Table.Tbody>
          </Table>
          </div>
        </Paper>
      )}

      {/* ═══════════════════════════════════════════════════════════
          CREATE MODAL
         ═══════════════════════════════════════════════════════════ */}
      <Modal
        opened={createOpen}
        onClose={() => setCreateOpen(false)}
        title="New Reimbursement Request"
        size="lg"
        closeOnClickOutside={false}
      >
        <Stack gap="sm">
          <Select
            label="HMGP Project"
            placeholder="Select project"
            required
            value={form.projectId}
            onChange={v => setForm(f => ({ ...f, projectId: v || '' }))}
            data={availableProjects.map(p => ({
              value: String(p.Rid),
              label: `${p.projectNumber} — ${p.projectTitle}`,
            }))}
            searchable
          />
          {form.projectId && (
            <Text size="xs" c="dimmed">
              RFR Number: <b>{nextRFRNumber(form.projectId)}</b>
            </Text>
          )}
          <Group grow>
            <TextInput
              label="Period Start"
              type="date"
              value={form.periodStart}
              onChange={e => setForm(f => ({ ...f, periodStart: e.target.value }))}
            />
            <TextInput
              label="Period End"
              type="date"
              value={form.periodEnd}
              onChange={e => setForm(f => ({ ...f, periodEnd: e.target.value }))}
            />
          </Group>

          <Divider label="Cost Breakdown" labelPosition="left" />

          {renderCostEditor(
            form.costItems,
            items => setForm(f => ({ ...f, costItems: items })),
            form.projectId,
          )}

          {/* Financial summary */}
          {form.projectId && (() => {
            const total = computeTotals(form);
            const { fedShare, applicantMatch, fedPct } = computeShares(total, form.projectId);
            return (
              <SimpleGrid cols={3} mt="xs">
                <Paper p="xs" withBorder ta="center">
                  <Text size="xs" c="dimmed">Total Requested</Text>
                  <Text fw={700}>{fmtCurrency(total)}</Text>
                </Paper>
                <Paper p="xs" withBorder ta="center">
                  <Text size="xs" c="dimmed">Federal Share ({fedPct}%)</Text>
                  <Text fw={700} c="var(--color-primary-700)">{fmtCurrency(fedShare)}</Text>
                </Paper>
                <Paper p="xs" withBorder ta="center">
                  <Text size="xs" c="dimmed">Applicant Match</Text>
                  <Text fw={700}>{fmtCurrency(applicantMatch)}</Text>
                </Paper>
              </SimpleGrid>
            );
          })()}

          <Textarea
            label="Notes"
            placeholder="Supporting details for this request..."
            value={form.notes}
            onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
            minRows={3}
          />
          <Group justify="flex-end" mt="sm">
            <Button variant="default" onClick={() => setCreateOpen(false)}>Cancel</Button>
            <Button color="var(--color-success)" onClick={handleCreate} loading={saving} leftSection={<IconSend size={16} />}>
              Create RFR
            </Button>
          </Group>
        </Stack>
      </Modal>

      {/* ═══════════════════════════════════════════════════════════
          DETAIL / EDIT MODAL
         ═══════════════════════════════════════════════════════════ */}
      <Modal
        opened={!!detailRfr}
        onClose={() => { setDetailRfr(null); setEditMode(false); }}
        title={
          <Group gap="sm">
            <IconReceipt size={20} />
            <Text fw={600}>{editMode ? 'Edit Reimbursement' : 'Reimbursement Detail'}</Text>
          </Group>
        }
        size="xl"
        closeOnClickOutside={false}
      >
        {detailRfr && (() => {
          const proj = projectMap[detailRfr.projectId];
          const costRows = parseCostBreakdown(detailRfr.costBreakdown);
          const reqAmt = num(detailRfr.requestedAmount);
          const fedShareAmt = num(detailRfr.federalShare);
          const appMatchAmt = num(detailRfr.applicantMatch);
          const approvedAmt = num(detailRfr.approvedAmount);
          const status = detailRfr.status;

          // ── EDIT MODE ──
          if (editMode) {
            const editTotal = computeTotals(editForm);
            const editShares = computeShares(editTotal, editForm.projectId || detailRfr.projectId);
            return (
              <Stack gap="sm">
                {/* Revision feedback banner */}
                {status === 'revision_needed' && detailRfr.reviewNotes && (
                  <Alert color="#F86A0B" title="Revision Requested" icon={<IconArrowBack size={16} />}>
                    {detailRfr.reviewedBy} ({detailRfr.reviewDate ? new Date(detailRfr.reviewDate).toLocaleDateString() : '—'}): {detailRfr.reviewNotes}
                  </Alert>
                )}

                <Group grow>
                  <TextInput
                    label="Period Start" type="date"
                    value={editForm.periodStart}
                    onChange={e => setEditForm(f => ({ ...f, periodStart: e.target.value }))}
                  />
                  <TextInput
                    label="Period End" type="date"
                    value={editForm.periodEnd}
                    onChange={e => setEditForm(f => ({ ...f, periodEnd: e.target.value }))}
                  />
                </Group>

                <Divider label="Cost Breakdown" labelPosition="left" />

                {renderCostEditor(
                  editForm.costItems,
                  items => setEditForm(f => ({ ...f, costItems: items })),
                  editForm.projectId || detailRfr.projectId,
                )}

                <SimpleGrid cols={3} mt="xs">
                  <Paper p="xs" withBorder ta="center">
                    <Text size="xs" c="dimmed">Total Requested</Text>
                    <Text fw={700}>{fmtCurrency(editTotal)}</Text>
                  </Paper>
                  <Paper p="xs" withBorder ta="center">
                    <Text size="xs" c="dimmed">Federal Share ({editShares.fedPct}%)</Text>
                    <Text fw={700} c="var(--color-primary-700)">{fmtCurrency(editShares.fedShare)}</Text>
                  </Paper>
                  <Paper p="xs" withBorder ta="center">
                    <Text size="xs" c="dimmed">Applicant Match</Text>
                    <Text fw={700}>{fmtCurrency(editShares.applicantMatch)}</Text>
                  </Paper>
                </SimpleGrid>

                <Textarea
                  label="Notes"
                  value={editForm.notes}
                  onChange={e => setEditForm(f => ({ ...f, notes: e.target.value }))}
                  minRows={2}
                />

                <Group justify="flex-end" mt="sm">
                  <Button variant="default" onClick={() => setEditMode(false)}>Cancel</Button>
                  <Button variant="light" color="var(--color-success)" loading={saving} onClick={() => handleSaveEdit(false)}>
                    Save
                  </Button>
                  {(status === 'draft' || status === 'revision_needed') && (
                    <Button color="var(--color-success)" loading={saving} onClick={() => handleSaveEdit(true)} leftSection={<IconSend size={16} />}>
                      Save & Submit
                    </Button>
                  )}
                </Group>
              </Stack>
            );
          }

          // ── READ-ONLY DETAIL ──
          return (
            <Stack gap="sm">
              {/* Header */}
              <Group justify="space-between">
                <Text fw={700} size="lg">{detailRfr.requestNumber}</Text>
                <Badge size="lg" color={STATUS_COLORS[status] || '#939598'} variant="light">
                  {STATUS_LABELS[status] || status}
                </Badge>
              </Group>

              {/* Project info */}
              {proj && (
                <Paper p="xs" withBorder bg="gray.0">
                  <Group justify="space-between">
                    <div>
                      <Text size="xs" c="dimmed">Project</Text>
                      <Text size="sm" fw={500}>{proj.projectNumber} — {proj.projectTitle}</Text>
                    </div>
                    <Button
                      size="xs" variant="subtle" color="var(--color-success)"
                      rightSection={<IconArrowRight size={14} />}
                      onClick={() => { setDetailRfr(null); navigate(`/pagrants/hmgp/projects/${proj.Rid}`); }}
                    >
                      View Project
                    </Button>
                  </Group>
                </Paper>
              )}

              {/* Period & dates */}
              <SimpleGrid cols={{ base: 1, sm: 2 }}>
                {(detailRfr.periodStart || detailRfr.periodEnd) && (
                  <div>
                    <Text size="xs" c="dimmed">Period</Text>
                    <Text size="sm">{detailRfr.periodStart ? new Date(detailRfr.periodStart).toLocaleDateString() : '\u2014'} to {detailRfr.periodEnd ? new Date(detailRfr.periodEnd).toLocaleDateString() : '\u2014'}</Text>
                  </div>
                )}
                <div>
                  <Text size="xs" c="dimmed">Request Date</Text>
                  <Text size="sm">{detailRfr.requestDate ? new Date(detailRfr.requestDate).toLocaleDateString() : '\u2014'}</Text>
                </div>
              </SimpleGrid>

              <Divider />

              {/* Financial summary */}
              <SimpleGrid cols={status === 'approved' || status === 'paid' ? 4 : 3}>
                <Paper p="xs" withBorder ta="center">
                  <Text size="xs" c="dimmed">Requested</Text>
                  <Text fw={700}>{fmtCurrency(reqAmt)}</Text>
                </Paper>
                <Paper p="xs" withBorder ta="center">
                  <Text size="xs" c="dimmed">Federal Share</Text>
                  <Text fw={700} c="var(--color-primary-700)">{fmtCurrency(fedShareAmt)}</Text>
                </Paper>
                <Paper p="xs" withBorder ta="center">
                  <Text size="xs" c="dimmed">Applicant Match</Text>
                  <Text fw={700}>{fmtCurrency(appMatchAmt)}</Text>
                </Paper>
                {(status === 'approved' || status === 'paid') && approvedAmt > 0 && (
                  <Paper p="xs" withBorder ta="center" style={{ borderTop: '3px solid #059669' }}>
                    <Text size="xs" c="dimmed">Approved</Text>
                    <Text fw={700} c="var(--color-success)" size="lg">{fmtCurrency(approvedAmt)}</Text>
                  </Paper>
                )}
              </SimpleGrid>

              {/* Cost breakdown table */}
              {costRows.length > 0 && (
                <>
                  <Divider />
                  <div>
                    <Text size="sm" fw={600} mb={4}>Cost Breakdown</Text>
                    <Table withTableBorder withColumnBorders>
                      <Table.Thead>
                        <Table.Tr>
                          <Table.Th>Description</Table.Th>
                          <Table.Th ta="right">Amount</Table.Th>
                        </Table.Tr>
                      </Table.Thead>
                      <Table.Tbody>
                        {costRows.map((row, i) => (
                          <Table.Tr key={i}>
                            <Table.Td>{row.description}</Table.Td>
                            <Table.Td ta="right">{fmtCurrency(row.amount)}</Table.Td>
                          </Table.Tr>
                        ))}
                        <Table.Tr>
                          <Table.Td fw={600}>Total</Table.Td>
                          <Table.Td ta="right" fw={600}>
                            {fmtCurrency(costRows.reduce((s, r) => s + num(r.amount), 0))}
                          </Table.Td>
                        </Table.Tr>
                      </Table.Tbody>
                    </Table>
                  </div>
                </>
              )}

              <Divider />

              {/* Submission info */}
              <SimpleGrid cols={{ base: 1, sm: 2 }}>
                {detailRfr.submittedBy && (
                  <div>
                    <Text size="xs" c="dimmed">Submitted By</Text>
                    <Text size="sm">{detailRfr.submittedBy}</Text>
                  </div>
                )}
                {detailRfr.submittedDate && (
                  <div>
                    <Text size="xs" c="dimmed">Submitted Date</Text>
                    <Text size="sm">{detailRfr.submittedDate ? new Date(detailRfr.submittedDate).toLocaleDateString() : '—'}</Text>
                  </div>
                )}
                {detailRfr.reviewedBy && (
                  <div>
                    <Text size="xs" c="dimmed">Reviewed By</Text>
                    <Text size="sm">{detailRfr.reviewedBy}</Text>
                  </div>
                )}
                {detailRfr.reviewDate && (
                  <div>
                    <Text size="xs" c="dimmed">Review Date</Text>
                    <Text size="sm">{detailRfr.reviewDate ? new Date(detailRfr.reviewDate).toLocaleDateString() : '—'}</Text>
                  </div>
                )}
              </SimpleGrid>

              {/* Status-specific alerts */}
              {status === 'approved' && (
                <Alert color="var(--color-success)" title="Approved" icon={<IconCheck size={16} />}>
                  Approved by {detailRfr.reviewedBy} on {detailRfr.reviewDate ? new Date(detailRfr.reviewDate).toLocaleDateString() : '—'} for {fmtCurrency(approvedAmt)}.
                  {detailRfr.reviewNotes ? ` Notes: ${detailRfr.reviewNotes}` : ''}
                </Alert>
              )}

              {status === 'denied' && (
                <Alert color="var(--color-error)" title="Denied" icon={<IconX size={16} />}>
                  Denied by {detailRfr.reviewedBy} on {detailRfr.reviewDate ? new Date(detailRfr.reviewDate).toLocaleDateString() : '—'}.
                  {detailRfr.reviewNotes ? ` Reason: ${detailRfr.reviewNotes}` : ''}
                </Alert>
              )}

              {status === 'revision_needed' && (
                <Alert color="#F86A0B" title="Revision Requested" icon={<IconArrowBack size={16} />}>
                  {detailRfr.reviewedBy} ({detailRfr.reviewDate ? new Date(detailRfr.reviewDate).toLocaleDateString() : '—'}): {detailRfr.reviewNotes || 'No details provided.'}
                </Alert>
              )}

              {status === 'paid' && (
                <Paper p="xs" withBorder bg="teal.0">
                  <SimpleGrid cols={{ base: 1, sm: 2 }}>
                    <div>
                      <Text size="xs" c="dimmed">Payment Date</Text>
                      <Text size="sm" fw={500}>{detailRfr.paymentDate ? new Date(detailRfr.paymentDate).toLocaleDateString() : '\u2014'}</Text>
                    </div>
                    <div>
                      <Text size="xs" c="dimmed">Payment Reference</Text>
                      <Text size="sm" fw={500}>{detailRfr.paymentReference || '\u2014'}</Text>
                    </div>
                  </SimpleGrid>
                </Paper>
              )}

              {/* Notes */}
              {detailRfr.notes && (
                <div>
                  <Text size="xs" c="dimmed">Notes</Text>
                  <Text size="sm">{detailRfr.notes}</Text>
                </div>
              )}

              <Divider />

              {/* ══════════════════════════════════════════════════
                  ACTION BUTTONS — status-dependent
                 ══════════════════════════════════════════════════ */}

              {/* DRAFT — edit, submit, delete */}
              {status === 'draft' && (
                <Group justify="flex-end" gap="sm">
                  <Button variant="default" onClick={() => { setDetailRfr(null); }}>Close</Button>
                  <Button color="var(--color-error)" variant="light" leftSection={<IconTrash size={16} />} onClick={() => handleDelete(detailRfr)}>
                    Delete
                  </Button>
                  <Button variant="light" color="var(--color-success)" leftSection={<IconEdit size={16} />} onClick={() => enterEditMode(detailRfr)}>
                    Edit
                  </Button>
                  <Button color="var(--color-success)" leftSection={<IconSend size={16} />} loading={saving} onClick={handleSubmit}>
                    Submit
                  </Button>
                </Group>
              )}

              {/* SUBMITTED — grantee can begin review, applicant sees status */}
              {status === 'submitted' && (
                <Group justify="flex-end" gap="sm">
                  <Button variant="default" onClick={() => setDetailRfr(null)}>Close</Button>
                  {!isApplicant && (
                    <Button color="var(--color-primary-700)" loading={saving} onClick={handleBeginReview}>
                      Begin Review
                    </Button>
                  )}
                  {isApplicant && <Text size="sm" c="var(--color-primary-700)" fs="italic">Awaiting grantee review</Text>}
                </Group>
              )}

              {/* UNDER REVIEW — grantee: approve, deny, request revision. Applicant: read-only */}
              {status === 'under_review' && isApplicant && (
                <Group justify="flex-end" gap="sm">
                  <Button variant="default" onClick={() => setDetailRfr(null)}>Close</Button>
                  <Text size="sm" c="yellow.8" fs="italic">Under grantee review</Text>
                </Group>
              )}
              {status === 'under_review' && !isApplicant && (() => {
                // Line-item review: auto-compute approved total from approved lines
                const approvedLineTotal = costRows.reduce((sum, row, i) =>
                  lineItemStatuses[i] === 'approved' || lineItemStatuses[i] === undefined ? sum + num(row.amount) : sum, 0);
                const rejectedLineTotal = costRows.reduce((sum, row, i) =>
                  lineItemStatuses[i] === 'rejected' ? sum + num(row.amount) : sum, 0);
                const effectiveApproved = adjustedAmount !== '' ? adjustedAmount : (costRows.length > 0 ? approvedLineTotal : reqAmt);

                return (
                  <Paper withBorder p="md">
                    <Stack gap="sm">
                      <Text fw={600}>Grantee Review</Text>

                      {/* Line-item review table */}
                      {costRows.length > 0 && (
                        <>
                          <Text size="sm" c="dimmed">Review each line item — click to approve or reject individually:</Text>
                          <Table withTableBorder withColumnBorders>
                            <Table.Thead>
                              <Table.Tr>
                                <Table.Th>Description</Table.Th>
                                <Table.Th ta="right">Requested</Table.Th>
                                <Table.Th ta="center" w={160}>Decision</Table.Th>
                              </Table.Tr>
                            </Table.Thead>
                            <Table.Tbody>
                              {costRows.map((row, i) => {
                                const itemStatus = lineItemStatuses[i];
                                return (
                                  <Table.Tr key={i} style={{
                                    background: itemStatus === 'rejected' ? 'var(--background-error-light)' :
                                      itemStatus === 'approved' ? 'rgba(3,119,48,0.08)' : undefined,
                                  }}>
                                    <Table.Td>
                                      <Text size="sm" td={itemStatus === 'rejected' ? 'line-through' : undefined} c={itemStatus === 'rejected' ? 'dimmed' : undefined}>
                                        {row.description}
                                      </Text>
                                    </Table.Td>
                                    <Table.Td ta="right">
                                      <Text size="sm" fw={500} td={itemStatus === 'rejected' ? 'line-through' : undefined}>
                                        {fmtCurrency(row.amount)}
                                      </Text>
                                    </Table.Td>
                                    <Table.Td>
                                      <Group gap={4} justify="center">
                                        <Button size="compact-xs"
                                          variant={itemStatus === 'approved' ? 'filled' : 'light'} color="var(--color-success)"
                                          onClick={() => setLineItemStatuses(prev => ({ ...prev, [i]: prev[i] === 'approved' ? undefined as any : 'approved' }))}>
                                          Approve
                                        </Button>
                                        <Button size="compact-xs"
                                          variant={itemStatus === 'rejected' ? 'filled' : 'light'} color="var(--color-error)"
                                          onClick={() => setLineItemStatuses(prev => ({ ...prev, [i]: prev[i] === 'rejected' ? undefined as any : 'rejected' }))}>
                                          Reject
                                        </Button>
                                      </Group>
                                    </Table.Td>
                                  </Table.Tr>
                                );
                              })}
                              <Table.Tr>
                                <Table.Td fw={600}>Approved Total</Table.Td>
                                <Table.Td ta="right" fw={700} c="var(--color-success)">{fmtCurrency(approvedLineTotal)}</Table.Td>
                                <Table.Td ta="center">
                                  {rejectedLineTotal > 0 && (
                                    <Badge size="sm" color="var(--color-error)" variant="light">-{fmtCurrency(rejectedLineTotal)} rejected</Badge>
                                  )}
                                </Table.Td>
                              </Table.Tr>
                            </Table.Tbody>
                          </Table>
                          <Group gap="xs">
                            <Button size="compact-xs" variant="subtle" color="var(--color-success)"
                              onClick={() => {
                                const all: Record<number, 'approved'> = {};
                                costRows.forEach((_, i) => { all[i] = 'approved'; });
                                setLineItemStatuses(all);
                              }}>
                              Approve All
                            </Button>
                            <Button size="compact-xs" variant="subtle" color="#939598"
                              onClick={() => setLineItemStatuses({})}>
                              Reset All
                            </Button>
                          </Group>
                        </>
                      )}

                      <NumberInput
                        label="Final Approved Amount"
                        description={costRows.length > 0 ? 'Auto-calculated from approved line items — override if needed' : 'Enter the approved amount'}
                        prefix="$"
                        thousandSeparator=","
                        min={0}
                        value={effectiveApproved}
                        onChange={v => setAdjustedAmount(typeof v === 'number' ? v : '')}
                      />
                      <Textarea
                        label="Review Notes"
                        placeholder="Comments for the applicant (include reasons for any rejected items)..."
                        value={reviewNotes}
                        onChange={e => setReviewNotes(e.target.value)}
                        minRows={3}
                      />
                      <Group justify="flex-end" gap="sm">
                        <Button variant="default" onClick={() => setDetailRfr(null)}>Close</Button>
                        <Button color="var(--color-error)" leftSection={<IconX size={16} />} loading={saving} onClick={handleDeny}>
                          Deny All
                        </Button>
                        <Button color="#F86A0B" leftSection={<IconArrowBack size={16} />} loading={saving} onClick={handleRequestRevision}>
                          Request Revision
                        </Button>
                        <Button color="var(--color-success)" leftSection={<IconCheck size={16} />} loading={saving}
                          onClick={async () => {
                            if (!detailRfr) return;
                            // Build review notes with line-item decisions
                            const rejectedItems = costRows
                              .map((row, i) => lineItemStatuses[i] === 'rejected' ? row.description : null)
                              .filter(Boolean);
                            let notes = reviewNotes;
                            if (rejectedItems.length > 0) {
                              notes = `${notes}${notes ? '\n\n' : ''}Rejected line items: ${rejectedItems.join('; ')}`;
                            }
                            const amt = adjustedAmount !== '' ? adjustedAmount as number : (costRows.length > 0 ? approvedLineTotal : reqAmt);
                            if (amt <= 0) {
                              notifications.show({ title: 'Error', message: 'Approved amount must be greater than zero', color: 'var(--color-error)' });
                              return;
                            }
                            setSaving(true);
                            try {
                              await updateReimbursement(detailRfr.Rid, {
                                status: 'approved',
                                approvedAmount: String(amt),
                                reviewedBy: user?.fullName || user?.email || '',
                                reviewDate: new Date().toISOString().split('T')[0],
                                reviewNotes: notes,
                              });
                              notifications.show({ title: 'Approved', message: `RFR approved for ${fmtCurrency(amt)}`, color: 'var(--color-success)' });
                              setReviewNotes('');
                              setAdjustedAmount('');
                              setLineItemStatuses({});
                              await refreshAndReselect(detailRfr.Rid);
                            } catch {
                              notifications.show({ title: 'Error', message: 'Failed to approve RFR', color: 'var(--color-error)' });
                            } finally {
                              setSaving(false);
                            }
                          }}>
                          Approve ({fmtCurrency(effectiveApproved)})
                        </Button>
                      </Group>
                    </Stack>
                  </Paper>
                );
              })()}

              {/* APPROVED — grantee can mark as paid, applicant sees status */}
              {status === 'approved' && isApplicant && (
                <Group justify="flex-end" gap="sm">
                  <Button variant="default" onClick={() => setDetailRfr(null)}>Close</Button>
                  <Text size="sm" c="var(--color-success)" fs="italic">Approved — awaiting payment</Text>
                </Group>
              )}
              {status === 'approved' && !isApplicant && (
                <Paper withBorder p="md">
                  <Stack gap="sm">
                    <Text fw={600}>Mark as Paid</Text>
                    <Group grow>
                      <TextInput
                        label="Payment Date"
                        type="date"
                        value={paymentDate}
                        onChange={e => setPaymentDate(e.target.value)}
                      />
                      <TextInput
                        label="Payment Reference (check #, wire ref)"
                        value={paymentRef}
                        onChange={e => setPaymentRef(e.target.value)}
                      />
                    </Group>
                    <Group justify="flex-end" gap="sm">
                      <Button variant="default" onClick={() => setDetailRfr(null)}>Close</Button>
                      <Button color="#03AC9E" leftSection={<IconCash size={16} />} loading={saving} onClick={handleMarkPaid}>
                        Mark as Paid
                      </Button>
                    </Group>
                  </Stack>
                </Paper>
              )}

              {/* PAID — read-only close */}
              {status === 'paid' && (
                <Group justify="flex-end">
                  <Button variant="default" onClick={() => setDetailRfr(null)}>Close</Button>
                </Group>
              )}

              {/* DENIED — read-only close */}
              {status === 'denied' && (
                <Group justify="flex-end">
                  <Button variant="default" onClick={() => setDetailRfr(null)}>Close</Button>
                </Group>
              )}

              {/* REVISION NEEDED — edit & resubmit */}
              {status === 'revision_needed' && (
                <Group justify="flex-end" gap="sm">
                  <Button variant="default" onClick={() => setDetailRfr(null)}>Close</Button>
                  <Button color="var(--color-success)" leftSection={<IconEdit size={16} />} onClick={() => enterEditMode(detailRfr)}>
                    Edit & Resubmit
                  </Button>
                </Group>
              )}
            </Stack>
          );
        })()}
      </Modal>

      {/* ── Cost Picker Modal ─────────────────────────────────── */}
      <Modal opened={costPickerOpen} onClose={() => setCostPickerOpen(false)} title="Select Cost Items to Include" size="lg">
        {(() => {
          const costs = projectCosts.filter(c => c.projectId === costPickerProjId);
          const prevReimbursed = previouslyReimbursedMap.get(costPickerProjId);
          if (costs.length === 0) {
            return <Text c="dimmed" ta="center" py="lg">No cost items found for this project.</Text>;
          }

          const allSelected = costs.every(c => selectedCostRids.has(c.Rid));
          const someSelected = costs.some(c => selectedCostRids.has(c.Rid));

          return (
            <Stack gap="sm">
              <Text size="sm" c="dimmed">
                Check the cost items to include in this reimbursement request.
                Items previously included in other RFRs are marked.
              </Text>

              <Table withTableBorder>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th w={40}>
                      <Checkbox
                        checked={allSelected}
                        indeterminate={someSelected && !allSelected}
                        onChange={() => {
                          if (allSelected) {
                            setSelectedCostRids(new Set());
                          } else {
                            setSelectedCostRids(new Set(costs.map(c => c.Rid)));
                          }
                        }}
                      />
                    </Table.Th>
                    <Table.Th>Type</Table.Th>
                    <Table.Th>Description</Table.Th>
                    <Table.Th ta="right">Amount</Table.Th>
                    <Table.Th>Previously Reimbursed</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {costs.map(c => {
                    const desc = `${c.costType || 'Cost'}: ${c.description || 'Untitled'}`;
                    const prevAmt = prevReimbursed?.totalByDesc.get(desc) || 0;
                    const wasPrevReimbursed = prevReimbursed?.descriptions.has(desc) || false;
                    const isChecked = selectedCostRids.has(c.Rid);
                    return (
                      <Table.Tr key={c.Rid}
                        style={{ cursor: 'pointer', opacity: wasPrevReimbursed ? 0.7 : 1 }}
                        onClick={() => {
                          setSelectedCostRids(prev => {
                            const next = new Set(prev);
                            if (next.has(c.Rid)) next.delete(c.Rid); else next.add(c.Rid);
                            return next;
                          });
                        }}>
                        <Table.Td><Checkbox checked={isChecked} readOnly /></Table.Td>
                        <Table.Td>
                          <Badge size="sm" variant="light">{c.costType || '—'}</Badge>
                        </Table.Td>
                        <Table.Td>
                          <Text size="sm">{c.description || '—'}</Text>
                        </Table.Td>
                        <Table.Td ta="right">
                          <Text size="sm" fw={500}>{fmtCurrency(c.totalCost)}</Text>
                        </Table.Td>
                        <Table.Td>
                          {wasPrevReimbursed ? (
                            <Badge size="sm" color="#F86A0B" variant="light">
                              {fmtCurrency(prevAmt)} in prior RFRs
                            </Badge>
                          ) : (
                            <Text size="xs" c="var(--color-success)">Not yet reimbursed</Text>
                          )}
                        </Table.Td>
                      </Table.Tr>
                    );
                  })}
                </Table.Tbody>
              </Table>

              <Group justify="space-between">
                <Text size="sm" c="dimmed">
                  {selectedCostRids.size} of {costs.length} selected
                  {selectedCostRids.size > 0 && ` — ${fmtCurrency(
                    costs.filter(c => selectedCostRids.has(c.Rid)).reduce((s, c) => s + num(c.totalCost), 0)
                  )}`}
                </Text>
                <Group gap="sm">
                  <Button variant="default" onClick={() => setCostPickerOpen(false)}>Cancel</Button>
                  <Button color="var(--color-success)" disabled={selectedCostRids.size === 0} onClick={handleCostPickerImport}>
                    Import Selected ({selectedCostRids.size})
                  </Button>
                </Group>
              </Group>
            </Stack>
          );
        })()}
      </Modal>
    </Stack>
  );
}
